/**
 * CodebaseAnalyzer — Extracts structural metadata from a project.
 *
 * Scans source files to build a lightweight model of the codebase:
 *   - Exported functions / classes / types per file
 *   - package.json scripts & dependencies
 *   - Route definitions (Express / Next / Fastify patterns)
 *   - Environment variables referenced
 *   - Directory structure summary
 *
 * The output feeds into DocMapper and DocGenerator so they know
 * *what* the code does and can keep docs in sync.
 */

import * as fs from 'fs/promises';
import * as path from 'path';
import { glob } from 'fast-glob';

// ─── Public types ────────────────────────────────────────────────────────────

export interface CodebaseSnapshot {
  projectName: string;
  framework: string | null;
  packageManager: 'npm' | 'yarn' | 'pnpm' | 'bun' | null;
  rootDir: string;
  timestamp: string;
  directories: string[];
  modules: ModuleInfo[];
  packageMeta: PackageMeta | null;
  routes: RouteEntry[];
  envVars: string[];
  stats: {
    totalFiles: number;
    totalExports: number;
    totalRoutes: number;
    totalEnvVars: number;
    languages: Record<string, number>;
  };
}

export interface ModuleInfo {
  file: string;
  exports: ExportEntry[];
  loc: number;
  language: string;
}

export interface ExportEntry {
  name: string;
  kind: 'function' | 'class' | 'type' | 'interface' | 'const' | 'enum' | 'default' | 'other';
  line: number;
  docComment?: string;
}

export interface PackageMeta {
  name: string;
  version: string;
  description?: string;
  scripts: Record<string, string>;
  dependencies: string[];
  devDependencies: string[];
}

export interface RouteEntry {
  method: string;
  path: string;
  file: string;
  line: number;
  handler?: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const IGNORE_DIRS = [
  'node_modules', '.git', 'dist', 'build', '.next', '.nuxt', '.output',
  'coverage', '.turbo', '.cache', '__pycache__', '.vibecheck',
  '.vercel', '.netlify', '.svelte-kit',
];

const EXPORT_PATTERNS = [
  { regex: /^export\s+(?:async\s+)?function\s+(\w+)/gm, kind: 'function' as const },
  { regex: /^export\s+class\s+(\w+)/gm, kind: 'class' as const },
  { regex: /^export\s+interface\s+(\w+)/gm, kind: 'interface' as const },
  { regex: /^export\s+type\s+(\w+)/gm, kind: 'type' as const },
  { regex: /^export\s+const\s+(\w+)/gm, kind: 'const' as const },
  { regex: /^export\s+enum\s+(\w+)/gm, kind: 'enum' as const },
  { regex: /^export\s+default\s+(?:function|class)?\s*(\w*)/gm, kind: 'default' as const },
];

const ROUTE_PATTERNS = [
  /(?:app|router)\.(get|post|put|patch|delete|options|head)\s*\(\s*['"`]([^'"`]+)['"`]/gi,
  /export\s+(?:async\s+)?function\s+(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\s*\(/gi,
  /fastify\.(get|post|put|patch|delete)\s*\(\s*['"`]([^'"`]+)['"`]/gi,
];

const ENV_PATTERN = /process\.env\.(\w+)|import\.meta\.env\.(\w+)|Deno\.env\.get\(['"`](\w+)['"`]\)/g;

// ─── Analyzer ────────────────────────────────────────────────────────────────

export class CodebaseAnalyzer {
  private rootDir: string;
  private ignorePatterns: string[];

  constructor(rootDir: string, extraIgnore: string[] = []) {
    this.rootDir = rootDir;
    this.ignorePatterns = [...IGNORE_DIRS.map(d => `**/${d}/**`), ...extraIgnore];
  }

  async analyze(): Promise<CodebaseSnapshot> {
    const [packageMeta, directories, sourceFiles] = await Promise.all([
      this.readPackageMeta(),
      this.listTopLevelDirs(),
      this.findSourceFiles(),
    ]);

    const modules: ModuleInfo[] = [];
    const allRoutes: RouteEntry[] = [];
    const envVarSet = new Set<string>();
    const langCounts: Record<string, number> = {};

    const BATCH = 50;
    for (let i = 0; i < sourceFiles.length; i += BATCH) {
      const batch = sourceFiles.slice(i, i + BATCH);
      const results = await Promise.all(batch.map(f => this.analyzeFile(f)));
      for (const r of results) {
        if (!r) continue;
        modules.push(r.module);
        allRoutes.push(...r.routes);
        r.envVars.forEach(v => envVarSet.add(v));
        langCounts[r.module.language] = (langCounts[r.module.language] || 0) + 1;
      }
    }

    return {
      projectName: packageMeta?.name || path.basename(this.rootDir),
      framework: this.detectFramework(packageMeta),
      packageManager: this.detectPackageManager(),
      rootDir: this.rootDir,
      timestamp: new Date().toISOString(),
      directories,
      modules,
      packageMeta,
      routes: allRoutes,
      envVars: [...envVarSet].sort(),
      stats: {
        totalFiles: modules.length,
        totalExports: modules.reduce((s, m) => s + m.exports.length, 0),
        totalRoutes: allRoutes.length,
        totalEnvVars: envVarSet.size,
        languages: langCounts,
      },
    };
  }

  // ─── Private ─────────────────────────────────────────────────────────────

  private async findSourceFiles(): Promise<string[]> {
    return glob(['**/*.{ts,tsx,js,jsx,mjs,cjs,py,go,rs}'], {
      cwd: this.rootDir,
      ignore: this.ignorePatterns,
      absolute: true,
    });
  }

  private async analyzeFile(absPath: string): Promise<{
    module: ModuleInfo; routes: RouteEntry[]; envVars: string[];
  } | null> {
    try {
      const content = await fs.readFile(absPath, 'utf-8');
      const relPath = path.relative(this.rootDir, absPath).replace(/\\/g, '/');
      const ext = path.extname(absPath);
      const lines = content.split('\n');

      return {
        module: {
          file: relPath,
          exports: this.extractExports(content, lines),
          loc: lines.length,
          language: this.extToLang(ext),
        },
        routes: this.extractRoutes(content, relPath),
        envVars: this.extractEnvVars(content),
      };
    } catch { return null; }
  }

  private extractExports(content: string, lines: string[]): ExportEntry[] {
    const exports: ExportEntry[] = [];
    const seen = new Set<string>();

    for (const { regex, kind } of EXPORT_PATTERNS) {
      const re = new RegExp(regex.source, regex.flags);
      let m: RegExpExecArray | null;
      while ((m = re.exec(content)) !== null) {
        const name = m[1] || 'default';
        if (seen.has(name)) continue;
        seen.add(name);
        const line = content.substring(0, m.index).split('\n').length;
        const docComment = this.extractDocComment(lines, line - 1);
        exports.push({ name, kind, line, docComment });
      }
    }
    return exports;
  }

  private extractDocComment(lines: string[], exportLine: number): string | undefined {
    let i = exportLine - 1;
    if (i < 0) return undefined;
    const buf: string[] = [];

    // Single-line comments
    while (i >= 0 && /^\s*\/\//.test(lines[i])) {
      buf.unshift(lines[i].replace(/^\s*\/\/\s?/, '').trim());
      i--;
    }
    if (buf.length > 0) return buf.join(' ');

    // Block comment
    if (i >= 0 && /\*\/\s*$/.test(lines[i])) {
      while (i >= 0) {
        buf.unshift(lines[i].trim().replace(/^\/?\*+\/?/, '').trim());
        if (/^\s*\/\*\*?/.test(lines[i])) break;
        i--;
      }
      return buf.filter(Boolean).join(' ');
    }
    return undefined;
  }

  private extractRoutes(content: string, relPath: string): RouteEntry[] {
    const routes: RouteEntry[] = [];
    for (const pattern of ROUTE_PATTERNS) {
      const re = new RegExp(pattern.source, pattern.flags);
      let m: RegExpExecArray | null;
      while ((m = re.exec(content)) !== null) {
        const method = m[1].toUpperCase();
        const routePath = m[2] || `/${relPath.replace(/\.(ts|tsx|js|jsx)$/, '')}`;
        const line = content.substring(0, m.index).split('\n').length;
        routes.push({ method, path: routePath, file: relPath, line });
      }
    }
    return routes;
  }

  private extractEnvVars(content: string): string[] {
    const vars: string[] = [];
    const re = new RegExp(ENV_PATTERN.source, ENV_PATTERN.flags);
    let m: RegExpExecArray | null;
    while ((m = re.exec(content)) !== null) {
      const name = m[1] || m[2] || m[3];
      if (name) vars.push(name);
    }
    return [...new Set(vars)];
  }

  private async readPackageMeta(): Promise<PackageMeta | null> {
    try {
      const raw = await fs.readFile(path.join(this.rootDir, 'package.json'), 'utf-8');
      const pkg = JSON.parse(raw);
      return {
        name: pkg.name || path.basename(this.rootDir),
        version: pkg.version || '0.0.0',
        description: pkg.description,
        scripts: pkg.scripts || {},
        dependencies: Object.keys(pkg.dependencies || {}),
        devDependencies: Object.keys(pkg.devDependencies || {}),
      };
    } catch { return null; }
  }

  private async listTopLevelDirs(): Promise<string[]> {
    try {
      const entries = await fs.readdir(this.rootDir, { withFileTypes: true });
      return entries
        .filter(e => e.isDirectory() && !IGNORE_DIRS.includes(e.name) && !e.name.startsWith('.'))
        .map(e => e.name).sort();
    } catch { return []; }
  }

  private detectFramework(pkg: PackageMeta | null): string | null {
    if (!pkg) return null;
    const all = [...pkg.dependencies, ...pkg.devDependencies];
    if (all.includes('next')) return 'nextjs';
    if (all.includes('nuxt') || all.includes('nuxt3')) return 'nuxt';
    if (all.includes('@sveltejs/kit')) return 'sveltekit';
    if (all.includes('express')) return 'express';
    if (all.includes('fastify')) return 'fastify';
    if (all.includes('react')) return 'react';
    if (all.includes('vue')) return 'vue';
    if (all.includes('@angular/core')) return 'angular';
    return null;
  }

  private detectPackageManager(): 'npm' | 'yarn' | 'pnpm' | 'bun' | null {
    try {
      const { existsSync } = require('fs');
      if (existsSync(path.join(this.rootDir, 'bun.lockb'))) return 'bun';
      if (existsSync(path.join(this.rootDir, 'pnpm-lock.yaml'))) return 'pnpm';
      if (existsSync(path.join(this.rootDir, 'yarn.lock'))) return 'yarn';
      if (existsSync(path.join(this.rootDir, 'package-lock.json'))) return 'npm';
    } catch { /* ignore */ }
    return null;
  }

  private extToLang(ext: string): string {
    const m: Record<string, string> = {
      '.ts': 'typescript', '.tsx': 'typescript',
      '.js': 'javascript', '.jsx': 'javascript', '.mjs': 'javascript', '.cjs': 'javascript',
      '.py': 'python', '.go': 'go', '.rs': 'rust',
    };
    return m[ext] || 'unknown';
  }
}
